import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';

import { Loading } from '../components/Loading';
import { getButtonClasses } from '@/components/ui/atoms/Button';
import { finalizeOnboarding, signupIntent, authReturnPath } from '../services/auth.service';
import { generateEventId, fireMetaPixelEvent } from '../services/tracking.service';

/**
 * Post-authentication landing — finalizes the user's onboarding intent
 * (plan + optional invitation token) and routes them to the right place.
 *
 * Replaces the old `onRedirectCallback` switch in Auth0Provider, which
 * had three branches and was hard to debug.
 */
export const AuthCallbackPage: React.FC = () => {
  const { isAuthenticated, isLoading, error, user, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const ranRef = useRef(false);
  const errorHandledRef = useRef(false);

  useEffect(() => {
    if (isLoading || ranRef.current) return;
    if (error) return;
    if (!isAuthenticated) return;
    ranRef.current = true;

    (async () => {
      try {
        try {
          fireMetaPixelEvent('Lead', {}, generateEventId());
        } catch {
          // tracking is best-effort
        }

        const intent = signupIntent.read();
        signupIntent.clear();

        if (!intent) {
          navigate('/home', { replace: true });
          return;
        }

        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          },
        });

        const planCode = intent.plan || 'free_trial';

        if (planCode === 'returning') {
          navigate('/home', { replace: true });
          return;
        }

        const result = await finalizeOnboarding({
          accessToken: token,
          plan_code: planCode,
          currency: intent.currency,
          invitation_token: intent.invitation_token,
          marketing_consent: intent.marketing_consent,
        });

        if (result.next_action === 'checkout' && result.checkout_url) {
          window.location.assign(result.checkout_url);
          return;
        }

        if (result.next_action === 'setup_card' && (result as any).redirect_path) {
          navigate((result as any).redirect_path, { replace: true });
          return;
        }

        navigate('/home', { replace: true });
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Could not finalize sign-in';
        toast.error(msg);
        navigate('/home', { replace: true });
      }
    })();
  }, [isAuthenticated, isLoading, error, user, getAccessTokenSilently, navigate]);

  // personal_email_blocked: silent redirect back to origin with a toast.
  // All other Auth0 errors fall through to the inline error UI below.
  useEffect(() => {
    if (error?.message !== 'personal_email_blocked' || errorHandledRef.current) return;
    errorHandledRef.current = true;

    toast.error('Please use your work email address. Personal email providers are not supported.');

    const returnPath = authReturnPath.read() || '/login';
    authReturnPath.clear();

    navigate(returnPath, { replace: true });
  }, [error, navigate]);

  if (error && error.message !== 'personal_email_blocked') {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-white">
        <div className="text-center max-w-md space-y-4 px-6">
          <h1 className="text-2xl font-bold text-dark-invirtus">
            Authentication issue
          </h1>
          <p className="text-sm text-dark-invirtus/70">
            {error.message ||
              "We couldn't complete sign-in. Please try again."}
          </p>
          <a
            href="/login"
            className={getButtonClasses(
              'contained',
              'secondary',
              'md',
              false,
              'w-full justify-center no-underline',
            )}
          >
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  return <Loading />;
};

export default AuthCallbackPage;
